
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navigation from '../home/components/Navigation';

const ServicesPage = () => {
  const services = [
    {
      id: 'ai-image',
      title: 'AI Image Generation',
      tagline: 'Visualize the Impossible',
      description: 'We will work with you to save you time and cost. Creating large photographic or video shoots can take days to complete. The shoot, the post-production all take time. The newest generative A.I. models can replace all of this. They are emerging. This is the future of image and video-driven content production. Stay ahead of your competition by producing content faster by working with us. Contact us to find out how we can help future-proof your content.   "Need bespoke visuals?" Our AI lets you experiment with unlimited styles and resolutions without paying for photo shoots.',
      icon: 'ri-image-ai-line',
      image: 'https://public.readdy.ai/ai/img_res/a1a7b6fc1e8e139711945a62b788c93d.jpg',
      link: '/services/ai-image-generation',
      gradient: 'from-brand-pink to-brand-aqua'
    },
    {
      id: 'ai-video',
      title: 'AI Video Generation',
      tagline: 'Stories That Move',
      description: '"Your products deserve cinematic storytelling." We produce compelling, high‑definition videos that capture attention on social media. "No expensive film crew required." AI‑assisted cinematography and editing drastically reduce production costs and turnaround times.',
      icon: 'ri-film-line',
      image: 'https://readdy.ai/api/search-image?query=Cinematic%20video%20production%20setup%20with%20AI%20technology%2C%20professional%20camera%20equipment%2C%20hot%20pink%20and%20aqua%20lighting%20effects%2C%20movie%20production%20studio%20atmosphere%2C%20dramatic%20lighting%2C%20high%20quality%20photography%2C%208k%20resolution&width=800&height=1000&seq=service-vid-1&orientation=portrait',
      link: '/services/ai-video-generation',
      gradient: 'from-brand-aqua to-brand-pink'
    },
    {
      id: 'hybrid-content',
      title: 'Hybrid AI Content',
      tagline: 'Best of Both Worlds',
      description: '"Existing content looks dated?" We clean up your audio, improve visuals and optimize your materials so you can breathe new life into past campaigns. "Need to repurpose assets?" Our tools intelligently remix your webinars, podcasts or tutorials into bite‑sized clips, carousels or reels.',
      icon: 'ri-magic-line',
      image: 'https://readdy.ai/api/search-image?query=Hybrid%20digital%20content%20creation%20showing%20blend%20of%20images%20and%20video%2C%20hot%20pink%20and%20aqua%20neon%20color%20scheme%2C%20futuristic%20creative%20workspace%2C%20holographic%20displays%2C%20professional%20studio%20lighting%2C%20ultra%20high%20quality%2C%208k%20detail&width=800&height=1000&seq=service-hyb-1&orientation=portrait',
      link: '/services/hybrid-ai-content',
      gradient: 'from-brand-pink to-brand-aqua'
    },
    {
      id: 'ugc-content',
      title: 'UGC Content',
      tagline: 'Authentic Voices',
      description: '"Audiences trust real voices." We curate authentic creator content that showcases your products in real‑life scenarios, boosting credibility. "Low engagement on your ads?" UGC generates higher engagement rates than brand‑made content, helping you build community and drive conversions.',
      icon: 'ri-user-heart-line',
      image: 'https://readdy.ai/api/search-image?query=Authentic%20user-generated%20content%20creation%20with%20real%20people%20filming%20reviews%20and%20tutorials%2C%20hot%20pink%20and%20aqua%20accent%20lighting%2C%20genuine%20social%20media%20content%20setup%2C%20diverse%20creators%2C%20natural%20lighting%2C%20high%20quality%20lifestyle%20photography&width=800&height=1000&seq=service-ugc-1&orientation=portrait',
      link: '/services/ugc-content',
      gradient: 'from-brand-aqua to-brand-pink'
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-aqua/5 to-transparent" />
        
        <div className="relative max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-7xl font-light mb-6">
              <span className="text-white">Our </span>
              <span className="bg-gradient-to-r from-brand-pink to-brand-aqua bg-clip-text text-transparent">Services</span>
            </h1>
            <p className="text-2xl text-white/60 max-w-3xl mx-auto">
              Cutting-edge AI technology meets creative excellence. Discover how we bring your vision to life.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24">
        <div className="max-w-[1400px] mx-auto px-12">
          <div className="space-y-32">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
              >
                <Link to={service.link}>
                  <div className={`grid ${index % 2 === 0 ? 'grid-cols-[1.2fr,1fr]' : 'grid-cols-[1fr,1.2fr]'} gap-12 items-center group cursor-pointer`}>
                    {/* Image */}
                    <div className={`relative ${index % 2 === 1 ? 'order-2' : ''}`}>
                      <div className="relative h-[600px] rounded-3xl overflow-hidden">
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        
                        {/* Floating Icon */}
                        <motion.div
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          className="absolute top-8 right-8 w-20 h-20 flex items-center justify-center rounded-2xl bg-black/40 backdrop-blur-xl border border-white/20"
                        >
                          <i className={`${service.icon} text-4xl text-brand-pink`}></i>
                        </motion.div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className={`${index % 2 === 1 ? 'order-1' : ''}`}>
                      <motion.div
                        whileHover={{ x: index % 2 === 0 ? 20 : -20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <p className={`text-sm font-semibold tracking-wider uppercase mb-4 bg-gradient-to-r ${service.gradient} bg-clip-text text-transparent`}>
                          {service.tagline}
                        </p>
                        
                        <h2 className="text-6xl font-light text-white mb-6">
                          {service.title}
                        </h2>
                        
                        <p className="text-xl text-white/70 mb-8 leading-relaxed">
                          {service.description}
                        </p>

                        <motion.div
                          whileHover={{ x: 10 }}
                          className={`inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r ${service.gradient} text-white font-semibold`}
                        >
                          <span>Explore Service</span>
                          <i className="ri-arrow-right-line text-xl"></i>
                        </motion.div>
                      </motion.div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32">
        <div className="max-w-[1400px] mx-auto px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-brand-pink/10 to-brand-aqua/10 border border-brand-pink/20 p-20 text-center"
          >
            <h2 className="text-5xl font-light text-white mb-6">
              Ready to Create Something Amazing?
            </h2>
            <p className="text-xl text-white/70 mb-10 max-w-2xl mx-auto">
              Let's discuss your project and bring your vision to life with AI-powered creativity.
            </p>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-12 py-5 bg-gradient-to-r from-brand-pink to-brand-aqua rounded-full text-white font-bold text-lg whitespace-nowrap cursor-pointer"
              >
                Start Your Project
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
